import { useCallback, useMemo, useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { RETRYABLE, measurementType, newId } from '@gk/contracts'
import { useClub, useEngineState } from '../../club/ClubProvider.tsx'
import { useNotebook } from '../../data/measure.ts'
import { DAILY_FIELDS, FIELD_TYPE, dailyDay, dayLabel, isMissing, lastDays, planDaily, prefill, reminderDue, siteReadings, type DailyDay, type DailyField, type DailyForm, type DailyProblem, type DayValue } from '../../data/dailyWeather.ts'
import { fmtNum } from '../../lib/format.ts'
import { localDate, noonOfLocalDay } from '../../lib/time.ts'
import type { GkDb } from '../../sync/db.ts'
import { BackLink, Banner, DisabledHint, Field, Pill, Section, errorText } from '../../ui/components.tsx'
import { useAction, useWriteAccess } from '../../ui/hooks.ts'
import { Picto, PictoBadge } from '../../ui/pictos.tsx'
import { unitSymbol } from './shared.tsx'

/** The daily weather reading, "Relevé du jour" (M21): the day's rainfall from the club's rain gauge and, once the thermometer is there, the day's lowest and highest air
 *  temperature, for the SITE. A quick card on Today and this page (from the card and from Mesures). One reading per type per club day: a first reading is the notebook's
 *  field fact (`measurement.record@1`: kept on the phone with no signal, shown at once); typing again on a day that has one is the notebook's correction
 *  (`measurement.correct@1`, online, `measurement.manage`), so the old value stays. The rules are `data/dailyWeather.ts`'s; the server decides. */

function useDailyReadings() {
  const nb = useNotebook()
  const by = useMemo(() => siteReadings(nb.readings), [nb.readings])
  return { ready: nb.ready, by }
}
/** Is today's rainfall still missing (M23 item 30: Today puts the missing reading among the direction's exceptions)? `due` is false until the notebook has answered. */
export function useDailyDue(): { ready: boolean; due: boolean } {
  const { site } = useClub()
  const d = useDailyReadings()
  if (!d.ready) return { ready: false, due: false }
  return { ready: true, due: reminderDue(dailyDay(d.by, localDate(new Date(), site.timezone))) }
}
/** A correction is an online intent: one the server did not answer in time must not stay in the outbox to be sent later behind the person's back, where a retry would
 *  aim a second correction at the same reading (MEASUREMENT_ALREADY_CORRECTED, and the day would end at the first value). Still waiting: it goes (true). Already on its
 *  way (or answered meanwhile): left to the engine (false), and the person looks at the list before trying again. */
const dropUnanswered = (db: GkDb, id: string): Promise<boolean> => db.transaction('rw', db.outbox, db.overlay, async () => {
  const row = await db.outbox.get(id)
  if (!row || !RETRYABLE.has(row.status)) return false
  await db.outbox.update(id, { status: 'discarded' }); await db.overlay.where('command_id').equals(id).delete()
  return true
})

/** A form opens on what the day holds; it opens again when that changes (a reading saved, another phone's arriving), not while somebody types. */
const signature = (d: DailyDay) => [d.date, ...[d.rain, d.tmin, d.tmax].map((v) => (v ? `${v.id}:${v.value}` : '-'))].join('|')

function problemText(t: (k: string, o?: Record<string, unknown>) => string, code: DailyProblem, field: DailyField): string {
  switch (code) {
    case 'DAILY_RAIN_REQUIRED': return t('measures.daily.problem.rainRequired')
    case 'DAILY_DECIMALS': return t('measures.daily.problem.decimals', { count: measurementType(FIELD_TYPE[field])!.decimals })
    case 'DAILY_MIN_ABOVE_MAX': return t('measures.daily.problem.minAboveMax')
    case 'DAILY_PENDING': return t('measures.daily.problem.pending')
    case 'DAILY_CORRECT_DENIED': return t('measures.daily.problem.correctDenied')
    case 'DAILY_CORRECT_OFFLINE': return t('measures.daily.problem.correctOffline')
    default: return errorText(t, code)
  }
}

/** One figure of a day: "2,5 mm", or a dash; with the pills that say it is not sent yet, or that the day has more than one. */
function Figure({ v, field }: { v: DayValue | null; field: DailyField }) {
  const { t, i18n } = useTranslation()
  const def = measurementType(FIELD_TYPE[field])!
  return (
    <span className="whitespace-nowrap" data-testid={`daily-${field}`} data-value={v ? String(v.value) : ''}>
      <span className="meta">{field === 'rain' ? t('measures.daily.short.rain') : field === 'tmin' ? t('measures.daily.short.tmin') : t('measures.daily.short.tmax')}</span>{' '}
      <span className="font-semibold">{v ? `${fmtNum(v.value, i18n.language, def.decimals)} ${unitSymbol(def.unit, i18n.language)}` : '—'}</span>
      {v?.pending && <> <Pill tone="amber">{t('measures.pill.pending')}</Pill></>}
      {v && v.count > 1 && <> <Pill tone="gray">{t('measures.daily.several', { count: v.count })}</Pill></>}
    </span>
  )
}
function DayFigures({ d }: { d: DailyDay }) {
  return <span className="flex flex-wrap gap-x-3 gap-y-1">{DAILY_FIELDS.map((f) => <Figure key={f} v={d[f]} field={f} />)}</span>
}

/** The form of one day. `onSaved` gets the sentence to show once the form has opened again on what was saved. */
function DailyEntry({ day, today, onSaved }: { day: DailyDay; today: string; onSaved: (message: string) => void }) {
  const { t, i18n } = useTranslation()
  const { send, sendAndWait, site, db } = useClub()
  const engine = useEngineState()
  const record = useWriteAccess('measurement.record')
  const manage = useWriteAccess('measurement.manage')
  const { busy, error, run } = useAction()
  const fmt = useCallback((n: number) => fmtNum(n, i18n.language, 4), [i18n.language])
  const initial = useMemo(() => prefill(day, fmt), [day, fmt])
  const [form, setForm] = useState<DailyForm>(initial)
  /** M23 item 17: a form that has just opened on an empty day does not shout "Saisissez la pluie" in red before the person has typed or tried to save;
   *  until then the grey save button says what it waits for (DisabledHint, item 6). */
  const [touched, setTouched] = useState(false)
  const online = engine.online !== false && navigator.onLine
  const plan = planDaily(form, initial, day, { mayCorrect: manage.allowed, online })
  const noThermometer = form.tmin.trim() === '' && form.tmax.trim() === '' && !day.tmin && !day.tmax
  const set = (f: DailyField, v: string) => { setTouched(true); setForm((x) => ({ ...x, [f]: v })) }
  const flipSign = (f: DailyField) => set(f, /^[-−]/.test(form[f].trim()) ? form[f].trim().slice(1) : `-${form[f].trim()}`)
  const onlyRainMissing = !plan.ok && plan.problems.rain === 'DAILY_RAIN_REQUIRED' && !plan.problems.tmin && !plan.problems.tmax

  function submit(e: FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (!plan.ok) return
    void run(async () => {
      // a past day is read at its noon on the club's clock; today is "now"
      const at = day.date === today ? undefined : noonOfLocalDay(day.date, site.timezone)
      let corrected = false
      // the corrections first, each waiting for the server's verdict: a refused one stops the save before anything else is sent, and its message stays on this form
      for (const a of plan.actions) if (a.kind === 'correct') {
        const v = await sendAndWait('measurement.correct@1', { measurement_id: newId(), corrects_measurement_id: a.correctsId, value: a.value, unit: measurementType(a.type)!.unit, reason: t('measures.daily.reason') })
        if (v.status === 'queued') throw new Error(await dropUnanswered(db, v.commandId) ? t('measures.daily.notSent') : t('measures.daily.late'))
        if (v.status !== 'applied' && v.status !== 'applied_with_exception') throw new Error(errorText(t, v.code))
        corrected = true
      }
      for (const a of plan.actions) if (a.kind === 'record') {
        await send('measurement.record@1', { measurement_id: newId(), measurement_type: a.type, value: a.value, unit: measurementType(a.type)!.unit, ...(at ? { observed_at: at } : {}) })
      }
      onSaved(corrected ? t('measures.daily.corrected') : online ? t('measures.daily.saved') : t('measures.daily.savedOffline'))
    })
  }
  const input = (f: DailyField, label: string, hint?: string) => {
    const def = measurementType(FIELD_TYPE[f])!
    const problem = f === 'rain' && !touched && plan.problems.rain === 'DAILY_RAIN_REQUIRED' ? undefined : plan.problems[f]
    const field = (
      <input id={`daily-${f}-input`} className="input min-w-0 flex-1 text-xl" inputMode="decimal" enterKeyHint={f === 'tmax' ? 'done' : 'next'} autoComplete="off" value={form[f]}
        aria-invalid={problem ? true : undefined} onChange={(e) => set(f, e.target.value)} disabled={!record.allowed} data-testid={`daily-${f}-input`} />
    )
    return (
      <Field label={`${label} (${unitSymbol(def.unit, i18n.language)})`} hint={plan.kept.includes(f) ? t('measures.daily.kept') : hint} error={problem ? problemText(t, problem, f) : null} htmlFor={`daily-${f}-input`}>
        {f === 'rain' ? field : (
          <div className="flex gap-2">
            {field}
            <button type="button" className="btn !min-w-14 !px-3 text-xl" aria-label={t('measures.daily.sign')} onClick={() => flipSign(f)} disabled={!record.allowed} data-testid={`daily-${f}-sign`}>±</button>
          </div>
        )}
      </Field>
    )
  }
  return (
    <form className="flex flex-col gap-3" onSubmit={submit} data-testid="daily-form" data-date={day.date} data-pending={[day.rain, day.tmin, day.tmax].some((v) => v?.pending) ? 'true' : 'false'}>
      {input('rain', t('measures.daily.rain'), t('measures.daily.rainHint'))}
      <div className="grid grid-cols-2 gap-3">
        {input('tmin', t('measures.daily.tmin'))}
        {input('tmax', t('measures.daily.tmax'))}
      </div>
      {noThermometer && <p className="text-sm opacity-75" data-testid="daily-no-thermometer">{t('measures.daily.noThermometer')}</p>}
      {error && <Banner tone="error"><span data-testid="daily-error">{error}</span></Banner>}
      <button className="btn btn-primary" disabled={busy || record.readOnlyMode || !record.allowed || !plan.ok} data-testid="daily-save">{t('measures.daily.save')}</button>
      <DisabledHint show={onlyRainMissing && !touched && record.allowed} testId="daily-save-hint">{t('measures.daily.rainToSave')}</DisabledHint>
    </form>
  )
}

/** On Today: the day's figures, or the form when the rainfall is not in yet (with the reminder pill). `compact` (M23 item 17, the jardinier's Today): one
 *  56-px row — "Relevé du jour · pas encore saisi", or the figures — that opens into the card on a tap, so the first job stays on the first screen; once
 *  opened, the card stays (a save closes the form, not the card, so "Relevé enregistré" is seen where the figures were typed; the row is back at the next visit). */
export function DailyWeatherCard({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation()
  const { site } = useClub()
  const d = useDailyReadings()
  const [expanded, setExpanded] = useState(false)             // the compact row opened into the card
  const [editing, setEditing] = useState(false)               // the form shown on a day that already has its reading
  const [done, setDone] = useState<string | null>(null)
  if (!d.ready) return null
  const today = localDate(new Date(), site.timezone)
  const day = dailyDay(d.by, today)
  const due = reminderDue(day)
  if (compact && !expanded) {
    return (
      <button type="button" className="row-link w-full text-left" onClick={() => { setExpanded(true); setEditing(true); setDone(null) }} data-testid="daily-card" data-state={due ? 'missing' : 'done'} data-compact="1" aria-expanded={false}>
        <PictoBadge name="weather" tone="water" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-lg font-semibold leading-snug">{t('measures.daily.title')}</span>
          <span className="meta">{due ? <span data-testid="daily-reminder">{t('measures.daily.missing')}</span> : <span data-testid="daily-summary"><DayFigures d={day} /></span>}</span>
        </span>
        <Picto name="chevron" className="size-5 shrink-0 opacity-40" />
      </button>
    )
  }
  return (
    <div className="card flex flex-col gap-3" data-testid="daily-card" data-state={due ? 'missing' : 'done'}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-3 text-lg font-semibold"><PictoBadge name="weather" tone="water" size="sm" />{t('measures.daily.title')}</span>
        {due ? <Pill tone="amber"><span data-testid="daily-reminder">{t('measures.daily.missing')}</span></Pill> : <Pill tone="green">{t('measures.daily.entered')}</Pill>}
      </div>
      {!due && !editing && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span data-testid="daily-summary"><DayFigures d={day} /></span>
          <button type="button" className="btn !px-4 text-sm" onClick={() => { setEditing(true); setDone(null) }} data-testid="daily-edit">{t('measures.edit')}</button>
        </div>
      )}
      {(due || editing) && <DailyEntry key={signature(day)} day={day} today={today} onSaved={(m) => { setDone(m); setEditing(false) }} />}
      {done && <Banner tone="ok"><span data-testid="daily-done">{done}</span></Banner>}
      <Link to="/measures/daily" className="btn" data-testid="daily-open">{t('measures.daily.lastDays')} →</Link>
    </div>
  )
}

/** The page: the form for today or a day that was forgotten (tap it in the list), and the last fourteen days with the missing ones shown as such. */
export function DailyWeatherPage() {
  const { t, i18n } = useTranslation()
  const { site, can } = useClub()
  const d = useDailyReadings()
  const today = localDate(new Date(), site.timezone)
  const [date, setDate] = useState(today)
  const [done, setDone] = useState<string | null>(null)
  const days = useMemo(() => lastDays(d.by, today), [d.by, today])
  if (!can('measurement.read')) return <section className="flex flex-col gap-4" data-testid="daily-page"><h1 className="text-2xl font-bold">{t('measures.daily.title')}</h1><Banner tone="warn">{t('measures.denied')}</Banner></section>
  if (!d.ready) return <p>{t('common.loading')}</p>
  const day = dailyDay(d.by, date)
  const pick = (next: string) => { if (next && next <= today) { setDate(next); setDone(null) } }
  return (
    <section className="flex flex-col gap-5" data-testid="daily-page">
      <BackLink to="/measures">← {t('measures.title')}</BackLink>
      <h1 className="text-2xl font-bold">{t('measures.daily.title')}</h1>
      <p className="meta">{t('measures.daily.help')}</p>
      {can('measurement.record') && (
        <div className="card flex flex-col gap-3" data-testid="daily-entry">
          <Field label={t('measures.daily.day')} hint={t('measures.daily.dayHint')} htmlFor="daily-day">
            <input id="daily-day" className="input" type="date" max={today} value={date} onChange={(e) => pick(e.target.value)} data-testid="daily-day" />
          </Field>
          <p className="font-semibold" data-testid="daily-day-label">{date === today ? t('measures.daily.today') : dayLabel(date, i18n.language, true)}</p>
          <DailyEntry key={signature(day)} day={day} today={today} onSaved={setDone} />
          {done && <Banner tone="ok"><span data-testid="daily-done">{done}</span></Banner>}
        </div>
      )}
      <Section title={t('measures.daily.lastDays')} help={can('measurement.record') ? t('measures.daily.listHelp') : undefined} testId="daily-list">
        <ul className="flex flex-col gap-2">
          {days.map((x) => (
            <li key={x.date}>
              <button type="button" className={`card flex w-full flex-col items-start gap-1 text-left ${x.date === date ? '!border-2 !border-brand dark:!border-fairway' : ''}`}
                onClick={() => pick(x.date)} aria-pressed={x.date === date} data-testid="daily-row" data-date={x.date} data-missing={isMissing(x) ? 'true' : 'false'}>
                <span className="flex w-full flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold">{x.date === today ? t('measures.daily.today') : dayLabel(x.date, i18n.language)}</span>
                  {isMissing(x) && <Pill tone="amber"><span data-testid="daily-row-missing">{t('measures.daily.noReading')}</span></Pill>}
                </span>
                {!isMissing(x) && <DayFigures d={x} />}
              </button>
            </li>
          ))}
        </ul>
      </Section>
    </section>
  )
}
